/**
 * COMP-53: Tournament Control Panel
 * Control panel for tournament management with bracket generation and status controls
 */

import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
  PlayIcon,
  PauseIcon,
  StopIcon,
  ArrowPathIcon,
  CogIcon,
  TrophyIcon,
  UserGroupIcon,
  CalendarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { Tournament } from '../../types/tournament';
import { tournamentsApi } from '../../services/api';
import toast from 'react-hot-toast';

interface TournamentControlPanelProps {
  tournament: Tournament;
  participantCount: number;
  matchCount: number;
  completedMatches: number;
  onBracketGenerate?: () => void;
  onTournamentStart?: () => void;
  onTournamentPause?: () => void;
  onTournamentComplete?: () => void;
}

export const TournamentControlPanel: React.FC<TournamentControlPanelProps> = ({
  tournament,
  participantCount,
  matchCount,
  completedMatches,
  onBracketGenerate,
  onTournamentStart,
  onTournamentPause,
  onTournamentComplete
}) => {
  const [showBracketOptions, setShowBracketOptions] = useState(false);
  const [bracketOptions, setBracketOptions] = useState({
    bracketType: tournament.bracketType || 'single_elimination',
    seedingStrategy: 'balanced_groups' as const,
    balanceGroups: true,
    randomSeed: Math.floor(Math.random() * 1000)
  });

  const queryClient = useQueryClient();

  // Generate bracket mutation
  const generateBracketMutation = useMutation(
    (options: any) => tournamentsApi.generateBracket(tournament.id, options),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tournament', tournament.id]);
        queryClient.invalidateQueries(['tournament-matches', tournament.id]);
        toast.success('Tournament bracket generated successfully!');
        setShowBracketOptions(false);
        onBracketGenerate?.();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to generate bracket');
      }
    }
  );

  // Tournament status update mutation
  const updateTournamentMutation = useMutation(
    (updates: Partial<Tournament>) => tournamentsApi.updateTournament(tournament.id, updates),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tournament', tournament.id]);
        toast.success('Tournament status updated successfully!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update tournament');
      }
    }
  );

  const handleGenerateBracket = () => {
    if (participantCount < 2) {
      toast.error('At least 2 participants are required to generate a bracket');
      return;
    }

    generateBracketMutation.mutate(bracketOptions);
  };

  const handleStartTournament = () => {
    updateTournamentMutation.mutate({ status: 'active' });
    onTournamentStart?.();
  };

  const handlePauseTournament = () => {
    updateTournamentMutation.mutate({ status: 'draft' });
    onTournamentPause?.();
  };

  const handleCompleteTournament = () => {
    if (completedMatches < matchCount) {
      toast.error('All matches must be completed before finishing the tournament');
      return;
    }
    updateTournamentMutation.mutate({ status: 'completed' });
    onTournamentComplete?.();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'completed': return 'text-blue-600 bg-blue-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getProgressPercentage = () => {
    if (matchCount === 0) return 0;
    return Math.round((completedMatches / matchCount) * 100);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
        <div className="flex items-center space-x-3">
          <CogIcon className="h-6 w-6 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Tournament Control</h3>
        </div>
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(tournament.status)}`}>
          {tournament.status.replace('_', ' ').toUpperCase()}
        </div>
      </div>

      {/* Tournament Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <UserGroupIcon className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium text-gray-700">Participants</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-1">{participantCount}</p>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <TrophyIcon className="h-5 w-5 text-green-600" />
            <span className="text-sm font-medium text-gray-700">Matches</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {completedMatches}/{matchCount}
          </p>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <CalendarIcon className="h-5 w-5 text-purple-600" />
            <span className="text-sm font-medium text-gray-700">Progress</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-1">{getProgressPercentage()}%</p>
        </div>
      </div>

      {/* Progress Bar */}
      {matchCount > 0 && (
        <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
          <div className="flex justify-between items-center text-sm text-gray-700 mb-3">
            <span className="font-medium">Tournament Progress</span>
            <span className="text-blue-600 font-semibold">{getProgressPercentage()}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
            <div
              className="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full transition-all duration-500 ease-out shadow-sm"
              style={{ width: `${getProgressPercentage()}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-600 mt-2">
            <span>{completedMatches} completed</span>
            <span>{matchCount - completedMatches} remaining</span>
          </div>
        </div>
      )}

      {/* Control Actions */}
      <div className="space-y-4">
        {/* Bracket Generation */}
        {tournament.status === 'draft' && (
          <div className="space-y-3">
            <button
              onClick={() => setShowBracketOptions(!showBracketOptions)}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              <ArrowPathIcon className="h-5 w-5" />
              <span>Generate Bracket</span>
            </button>

            {showBracketOptions && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <h4 className="font-medium text-gray-900">Bracket Options</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bracket Type
                    </label>
                    <select
                      value={bracketOptions.bracketType}
                      onChange={(e) => setBracketOptions(prev => ({ ...prev, bracketType: e.target.value as any }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="single_elimination">Single Elimination</option>
                      <option value="double_elimination">Double Elimination</option>
                      <option value="round_robin">Round Robin</option>
                      <option value="swiss">Swiss System</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Seeding Strategy
                    </label>
                    <select
                      value={bracketOptions.seedingStrategy}
                      onChange={(e) => setBracketOptions(prev => ({ ...prev, seedingStrategy: e.target.value as any }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="balanced_groups">Balanced Groups</option>
                      <option value="rating_based">Rating Based</option>
                      <option value="historical_performance">Historical Performance</option>
                      <option value="random">Random</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="balanceGroups"
                    checked={bracketOptions.balanceGroups}
                    onChange={(e) => setBracketOptions(prev => ({ ...prev, balanceGroups: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="balanceGroups" className="text-sm text-gray-700">
                    Balance groups for fair competition
                  </label>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={handleGenerateBracket}
                    disabled={generateBracketMutation.isLoading || participantCount < 2}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {generateBracketMutation.isLoading ? 'Generating...' : 'Generate'}
                  </button>
                  <button
                    onClick={() => setShowBracketOptions(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 font-medium"
                  >
                    Cancel
                  </button>
                </div>

                {participantCount < 2 && (
                  <div className="flex items-center space-x-2 text-amber-600 bg-amber-50 p-3 rounded-lg">
                    <ExclamationTriangleIcon className="h-5 w-5" />
                    <span className="text-sm">At least 2 participants required to generate bracket</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Tournament Status Controls */}
        <div className="flex space-x-3">
          {tournament.status === 'draft' && matchCount > 0 && (
            <button
              onClick={handleStartTournament}
              disabled={updateTournamentMutation.isLoading}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
            >
              <PlayIcon className="h-5 w-5" />
              <span>Start Tournament</span>
            </button>
          )}

          {tournament.status === 'active' && (
            <>
              <button
                onClick={handlePauseTournament}
                disabled={updateTournamentMutation.isLoading}
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-medium"
              >
                <PauseIcon className="h-5 w-5" />
                <span>Pause</span>
              </button>

              <button
                onClick={handleCompleteTournament}
                disabled={updateTournamentMutation.isLoading || completedMatches < matchCount}
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                <StopIcon className="h-5 w-5" />
                <span>Complete</span>
              </button>
            </>
          )}
        </div>

        {/* Completion Warning */}
        {tournament.status === 'active' && completedMatches < matchCount && (
          <div className="flex items-center space-x-2 text-amber-600 bg-amber-50 p-3 rounded-lg">
            <ExclamationTriangleIcon className="h-5 w-5" />
            <span className="text-sm">
              Complete all matches ({matchCount - completedMatches} remaining) before finishing the tournament
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TournamentControlPanel;
